import * as uuid from "uuid";
import * as moment from "moment";
import * as _ from "lodash";
import * as Consts from "./rlAdmin.consts";

export interface IDictionaryS<T> {
	[key: string]: T;
}

export interface IDictionaryN<T> {
	[key: number]: T;
}

export interface IRLCharDataElementScope extends ng.IScope {
	cmd: ICharacteristicMetaData;
	charDatas: ICharDataValue[];
	charLovMetaData: ICharLovMetaData;
	editMode: boolean;
	searchMode: boolean;
	parentCurrencyCharData?: ICharDataValue;
	matchCurrencyCharData?: ICharDataValue;
	currencySymbolCharData?: ICharDataValue;
	createMode: boolean;
	submitted?: boolean;
	firstCharData: ICharDataValue;
	templateID: number;
	entityID?: string;
	bulkEdit: boolean;
	lovDelimiter?: string;
	disableControlValidation?: boolean;
	parentEntityID?: string;
	bulkUpdate?: boolean;
}

//#region Services

export interface IPromised<T> {
	$promise: ng.IPromise<T>
}

export interface PagedEntities<T> {
	numFound?: number;
	pages?: number;
	pagingOptions: IPagingOptions;
	results?: T[];
}

//#endregion

//#region Search

export interface ISearchOperation {
	charTypeID: number;
	operator?: Consts.AdvancedSearch.Operator;
	comparator: Consts.AdvancedSearch.Comparator;
	// array for the ng-repeat hack because rl-char-data-element does not support re-initialization when metadata or chardata changes
	characteristicMetaData: ICharacteristicMetaData[];
	charDatas: ICharDataValue[];
	betweenCharDatas?: ICharDataValue[];
}

export interface ISearchGroup {
	operator: Consts.AdvancedSearch.Operator;
	operations: ISearchOperation[];
}

export interface ISaveSearchModel {
	id?: string;
	name: string;
	showOnDashboard: boolean;
	roleIds?: number[];
	searchRequest?: IAdvancedSearchModel;
}

export interface IAdvancedSearchModel {
	charTypeID: number;
	searchGroups: ISearchGroup[];
	keywords: string;
	rowCount: number;
	page: number;
	templateGroupIDs?: number[];
	template?: string;
	status?: string;
	sortDir?: number;
	sortField?: string;
	relatedToMe?: boolean;
	active?: boolean;
	relatedEntityID?: string;
	associateToTemplate?: string;
	notChildrenOfEntityID?: string;
	excludeEntityID?: string;
	explicitRelationshipsOnly?: boolean;
	filterByEntityID?: string;
	groups?: string[];
	facetFields?: string[];
	activeIndicator?: boolean;
	filterByTemplateIds?: number[];
	selectFields?: string[];
}

export interface IAdvanceSearchRelationshipModel {
	advanceSearchRequest: IAdvancedSearchModel;
	childCharTypeID: number;
	allRelatedToModuleLevelEntity: number;
	allRelatedToNonModuleLevelEntity: number;
	childRecordID: number;
	childRecordTemplateID: number;
	showParent: boolean;
	showChildren: boolean;
	relationshipIndicator: number;
}

export interface ISearchExportModel {
	searchRequest: IAdvancedSearchModel;
	headerColumns: ICustomUserColumns;
}

export interface ISearchRequest {
	k: string;
	t: string;
	s: string;
	p: number;
	r: number;
	sf: string;
	sd: number;
	at?: string;
	nco?: string;
	tgs?: number[];
	rtm?: boolean;
	neg?: boolean;
	act?: boolean;
	fid?: string;
	ct?: number;
	pid?: number;
	tid?: number;
}

export interface IAdvancedSearchMetaData {
	parties: IParty[];
	relatedCharTypeIDs: number[];
	templateCharacteristicMetaDatas: ITemplateCharacteristicMetaData[];
	//headerCharacteristicMetaDatas { get; set; }
	//headerLovs { get; set; }
}

export interface ISearchFacet {
	count: string;
	key: string;
	value: string;
}

export interface IGrouping {
	groups: IGroup[];
}

export interface IGroup {
	documents: IEntitySearchDoc[];
	groupValue: string;
	numFound: number;
}

export class AdvancedSearchResult {
	documents: IEntitySearchDoc[];
	numFound: number;
	qTime: number;
	facetResults: { [key: string]: Array<ISearchFacet> };
	spellCheckResults: ISpellCheckResult[];
	grouping: IGrouping;

	getTemplateFacets = () => {
		return this.GetFacet("template_meta");
	}

	getStatusFacets = () => {
		return this.GetFacet("status_meta");
	}

	getGenreFacets = () => {
		return this.GetFacet("genre_meta");
	}

	public GetFacet(name: string) {
		return _.map(this.facetResults[name], (item) => {
			return {
				key: item.key,
				value: item.key.substring(0, item.key.indexOf('|')) + ' (' + item.value + ')'
			};
		});
	}
}

export interface ISpellCheckResult {
	suggestions: string[];
	numFound: number;
}

export interface ISearchResults<T> {
	numFound: number;
	qTime: number;
	pageCount: number;
	page: number;
	resultDocs: Array<T>;
	templateFacets: Array<IKeyValuePair<string, number>>;
	statusFacets: Array<IKeyValuePair<string, number>>;
	genreFacets: Array<IKeyValuePair<string, number>>;
}

export interface IDynamicReportRequest {
	ct: number;
	t: number;
	cols: string[];
	relIndId: number;
	p: number;
	r: number;
	sf: string;
	sd: number;
}

export interface ISavedSSRSReport {
	savedReportName: string;
	reportParameters: IReportParameter[];
	reportParametersJson: string;
}

export interface ISaveReportCriteria {
	id?: string;
	nameSpace: string;
	reportName: string;
	savedReports: ISavedSSRSReport[];
	savedReportsJson: string;
	isGlobal: number;
}

export interface IReportParamSelectedCriteriaScope extends ng.IScope {
	reportParameters: IReportParameter[];
	reportName: string;
	isValid: boolean;
	divId: number;
	mode: string;
	selectedSavedReport: ISavedSSRSReport;
}

export interface IReport {
	divID: number;
	reportName: string;
	reportPath: string;
	displayReportName: string;
	group: string;
}

export interface IReportGroup {
	groupName: string;
	reports?: IReport[];
}

export interface IReportParameter {
	parameterName: string;
	parameterPrompt: string;
	parameterType: string;
	parameterValue: string;
	allowBlank: boolean;
	allowMultiple: boolean;
	selectedParameterValue: string;
	selectedParameterDisplay: string;
	selectedRecIdTitlePairs: IKeyValuePair<number, string>[];
}

export interface IDynamicReportResults extends ISearchResults<IEntitySearchDoc> {
	relatedEntities: IDynamicReportResults[];
}

export interface IAdHocFilterTreeNode {
	label: string;
	value: string;
	id: string;
	children: IAdHocFilterTreeNode[];
}

export interface IKeyValuePair<T, U> {
	key: T;
	value: U;
}

export interface IFacetResults {
	[key: string]: IKeyValuePair<string, string>[]
}

export interface IWorkOrderSearchDoc {
	workOrderID: number;
	revisionID: number;
	title: string;
	status: string;
	contact: string;
	company: string;
	date: string;
}

export interface IProjectSearchDoc {
	projectID: number;
	revisionID: number;
	title: string;
	status: string;
	contact: string;
	company: string;
	date: string;
}


export interface IContractSearchDoc {
	contractID: number;
	revisionID: number;
	title: string;
	status: string;
	contact: string;
	company: string;
	date: string;
}

export interface IContactSearchResults extends ISearchResults<IContactSearchDoc> {
	workflowActions: Array<IKeyValuePair<string, number>>;
}

export interface IContactSearchDoc {
	entityID: string;
	contactID: number;
	name: string;
	email: string;
	contactOrg: string;
	status: string;
	template: string;
}

export interface IPasswordReset {
	Email: string;
	Success: boolean;
}

export interface ITypeAheadSearchResults extends ISearchResults<IContactSearchDoc> {
	workflowActions: Array<IKeyValuePair<string, number>>;
}

export interface IAssetSearchResult extends ISearchResults<IAssetSearchDoc> {
	workflowActions: Array<IKeyValuePair<string, number>>
}

export interface IWFEntityStatusResult {
	statuses: IWFStepConfig[];
}

export interface IWFActionExtension {
	divID: number;
	workflowExtensionID: number;
	processID: number;
	actionID: number;
	workflowExtensionTypeID: number;
	workflowExtensionPathID: number;
}

export interface IWFExtensionPath {
	pathID: number;
	pathTypeID: number;
	label: string;
	path: string;
}
export interface IWFActionExtensionResult {
	extensions: IWFActionExtension[];
	paths: IWFExtensionPath[];
}

export interface IAssetSearchDoc {
	entityID: string;
	assetID: number;
	title: string;
	status: string;
	template: string;
}


export interface IAssetAvailsSearchResult extends ISearchResults<IAssetAvailableRightsResult> {
}

export interface IAssetAvailableRightsResult {

	propertyID: number;
	propertyTitle: string;
	propertyTypeLabel: string;
	propertyCharLabel: string;
	startDate: Date;
	endDate: Date;
	exclusive: string;
	territory: string;
	media: string;
	language: string;
	channel: string;
}

export interface IFinancialSearchDoc {
	entityID: string;
	invoiceID: number;
	contractID: number;
	billingContact: string;
	dueDate: string;
	total: number;
	balance: number;
	status: string;
	template: string;
	currency: string;
}

export interface IEntitySearchRequest {
	ct: number;
	k: string;
	t: string;
	p: number;
	r: number;
	s?: string[];
	sf: string;
	sd: number;
	fid?: string;
	tgs?: number[];
	ere?: string;
	ogre?: string;
	ee?: string;
	ftids?: number[];
	rtm?: boolean;
}

export interface ICreateSalesSheetRequest {
	templateRecordID: number;
	title: string;
	assetIDs: number[];
	rightSet: {
		templateID: number;
		startDate: Date;
		endDate: Date;
		territoryIDs: number[];
		mediaIDs: number[];
	}
}

export interface ICatalogAvailsQuery {
	exactIndicator: number;
	startDate: string;
	endDate: string;
	territoryIds: number[];
	mediaIds: number[];
	channelIds: number[];
	languageIds: number[];
	genreIds: number[];
	templateIds: number;
	statusIds: number;
	recordIds?: number[];
	titleQuery?: string;
}

export interface ICatalogAvailsDocument extends IEntitySearchDoc {
	selected: boolean;
}

export interface IEntitySearchResults {
	numFound: number;
	qTime: number;
	pageCount: number;
	resultDocs: IEntitySearchDoc[];
	templateFacets: IKeyValuePair<string, number>[];
	statusFacets: IKeyValuePair<string, number>[];
}

export interface IRightsFilterModel {
	startDate: string;
	endDate: string;
	selectedAssetFacet?: { [facetName: string]: string[] };
	exclusive: string;
	exactInd?: string;
	isWindowed?: boolean;
	selectedRightSetTemplateIDs?: number[];
	selectedTerritoryIDs: number[];
	selectedMediaIDs: number[];
	selectedLanguageIDs: number[];
	selectedChannelIDs: number[];
	selectedTerritoryLeafIDs?: number[];
	selectedMediaLeafIDs?: number[];
	selectedLanguageLeafIDs?: number[];
	selectedChannelLeafIDs?: number[];
	selectedTerritoryPaths?: string[];
	selectedMediaPaths?: string[];
	selectedLanguagePaths?: string[];
	selectedChannelPaths?: string[];
	selectedTemplateID?: number;
	selectedStatusID?: number;
	titleKeywords?: string;
	titleKeywordsFooter?: string;
	rollup?: number;
	show?: {
		dealIDs?: boolean;
		clients?: boolean;
		territories?: boolean;
		medias?: boolean;
		channels?: boolean;
		languages?: boolean;
	};
	assetCharID?: number;
	selectedAssetCharValueIDs?: number[];
	selectedAssetRecordIDs?: number[];
	minDays?: number;
	matchType?: number;
	isAssetAvailable?: boolean;
	isTupleAvailable?: boolean;
	isScoped?: boolean;
	rollupDim?: number;
	truncateWindows?: boolean;
	dimFormat: _.Dictionary<boolean>;
}

export interface IAvailsSavedSelectedCriteriaModel {
	criteriaName: string;
	mode: string;
	selectedFilterModel: IRightsFilterModel;
}

//#endregion

//#region Contracts
export interface IContractAssetRelationshipPair extends IEntityRelationshipPair {
	usages: Array<IUsageRelationshipPair>;
}

export interface INestedRelationshipPair extends IEntityRelationshipPair {
	assetRels: Array<IEntityRelationship>;
	contactRels: Array<IEntityRelationship>;
}

export interface IContractRightDocumentRelationshipPair extends IDocumentRelationshipPair {
	assetRels: Array<IEntityRelationship>;
	contactRels: Array<IEntityRelationship>;
}

export interface IContractRightRelationship {
	relationship: IEntityRelationship;
	assetRels: Array<IEntityRelationship>;
}

export interface IUsageRelationshipPair extends IEntityRelationshipPair {
	amounts: Array<IAmountRelationshipPair>;
	assetRels: Array<IEntityRelationship>;
	projectRels: Array<IEntityRelationship>;
	contactRels: Array<IEntityRelationship>;
	mockRels: Array<IEntityRelationship>;
	// TODO: Figure out the right way to do this that can be universal 
	// between IUsageRelationshipPair, IContractRightRelationshipPair...
	// as these are related to rlEntityRelationships directive
	assetEntities: Array<IEntity>;
}

export interface IAmountRelationshipPair extends IEntityRelationshipPair {
	invoiceRecID: number;
	assetRels: Array<IEntityRelationship>;
}

export interface IRoyaltySplit {
	usageID: number;
	payeeID: number;
	payeeName: string;
	incomeGroupID: number;
	incomeGroupName: string;
	splitPercentage: number;
	aboveTheLine: boolean;
}

export interface IContractRoyaltyRelationshipPair extends IEntityRelationshipPair {
	assets: IEntityRelationshipPair[];
	royaltySplits: IRoyaltySplit[];
}

export interface IRoyaltyHeader {
	label: string;
	tagLabel: string;
}

export interface IContactParty {
	party: IParty;
	contacts: Array<IEntityRelationshipPair>;
}

export interface IContactSearchModel {
	contactID: number;
	name: string;
	parentCharTypeID: number;
	charDataCollection: ICharDataCollection;
	sequenceNumber: number;
}

export interface IPartyWithNotification {
	divID?: number;
	actionID?: number;
	messageID?: number;
	subject?: string;
	body?: string;
	bodyHTML?: string;
	previewIndicator?: number;
	partyID: number;
	notifyChildren: boolean;
}

export interface IParty {
	divisionID: number;
	charTypeID: number;
	templateID: number;
	partyID: number;
	partyGroupID: number;
	partyName: string;
	partyDescription: string;
	requiredIndicator: number;
	multipleIndicator: number;
	editIndicator: number;
	deleteIndicator: number;
	primaryIndicator: number;
	visibilityIndicator: number;
	sequenceNumber: number;
	createdBy: number;
	createdAt: string;
	updatedBy: number;
	updatedAt: string;
}

export interface IPartyGroup {
	divisionID: number;
	groupID: number;
	groupLabel: string;
	groupDescription: string;
	expandIndicator: number;
	expandGroupIndicator: number;
	systemIndicator: number;
	visibilityIndicator: number;
	sequenceNumber: number;
	userId: number;
}

export interface IEntityDocumentsCollection {
	downloadAttachments: IEntityAttachment[];
	uploadedDocuments: IEntityCharDataPair[];
	sharedDocuments: IEntityCharDataPair[];
	sharedFileRecIDToParentIDDict: { [key: number]: IKeyValuePair<string, string> };
	recordDocuments: IRecordDocument[];
	attachmentUrls: { [key: number]: string };
	documentUrls: { [key: number]: string };
	recordUrls: { [key: string]: string };
	contactTitles: { [key: number]: string };
	entityFileContactRels: { [key: number]: { [key2: number]: number } }
	docIDToRelRecID: { [key: number]: number };
	folders: IEntityCharDataPair[];
}

export interface IGetAssociableInvoicesRequest {
	contractID: number;
	amountTemplateIDs: number[];
	currencyCharValueID?: number;
}

export interface IAssociableInvoiceResults {
	invoices: IEntity[];
	templates: ICharacteristicTemplate[];
}

export interface IUsageAmountDocumentResults {
	usageAmountDocuments: IUsageAmountDocument[];
}

export interface IAssociateAmountsToInvoiceRequest {
	invoiceID: number;
	amountIDs: number[];
}

export interface IAssociateAmountsToNewInvoiceRequest {
	contractID: number;
	invoiceTemplateID: number;
	amountIDs: number[];
}

export interface IUpdateProjectContactCharacteristicsRequest {
	charTypeID: number;
	contactID: number;
	recordID: number;
	updateProfile: boolean;
	contactCharDatas: ICharDatas;
}


//#endregion

//#region Contacts

export interface IExecuteContactWorkflowResults {
	successContactIDs: number[];
	errorContactIDs: number[];
}

export interface IApplicationInfo {
	urlTypeID: number;
	appUrlID: number;
	url: string;
	label: string;
}

//export interface IAccountGroup {
//    groupID: number;
//    groupDescription: string;
//}

export interface IAccountRole {
	roleID: number;
	roleLabel: string;
	superUserIndicator: number;
	sequenceNumber: number;
}

export interface ITransactionAttachment {
	created: string;
	attachmentLabel: string;
	attachmentID: number;
	attachmentRevisionID: number;
	createdBy: number;
	fileName: string;
	filePath: string;
}

export interface IEntityAttachment {
	created: string;
	attachmentLabel: string;
	attachmentID: number;
	attachmentRevisionID: number;
	createdBy: number;
	fileName: string;
	filePath: string;
}


export interface IDocumentRevisionCollection {
	documentRevisions: IDocumentRevision[];
	documentUrls: { [key: string]: string };
}
export interface IDocumentRevision {
	documentID: string;
	revisionID: number;
	revisionlabel: string;
	revisionDate: string;
	createdBy: number;
	charDataCollection: ICharDataCollection;
	isPreviewable: boolean;
	record: IEntity;
}
export interface IRecordDocument {
	documentID: number;
	documentRevisionID: number;
	templateTypeID: number;
	filename: string;
	templateLabel: string;
	username: string;
	createdAt?: string;
	invoiceID?: number;
}

export interface IUserApplicationInfo {
	divisionID: number;
	appUrlID: number;
	divisionLabel: string;
	appUrlLabel: string;
	userAppStatusID: number;
	appUrlTypeID: number;
}

export interface IUserSecurity {
	divID: number;
	userID: number;
	roles: IAccountRole[];
	//groups: IAccountGroup[];
	applications: IUserApplicationInfo[];
}

export interface IGetContractRightsResponse {
	groupedRightSets: { [templateID: string]: IGroupedRights<IContractRightDocumentRelationshipPair> };
	templateMetaData: { [templateID: string]: ICharacteristicMetaDataCollection };
	facetResults: { [key: string]: Array<ISearchFacet> };
}

export interface IGroupedRights<T> {
	numFound: number;
	rightSets: T[];
}

export interface ISearchContractAssetsResponse {
	numFound: number;
	assets: IRecordRelationshipPairChild[];
	assetChildCounts: { [key: number]: number };
}

export interface ISearchCatalogRightsResponse {
	groupedRightSets: { [templateID: string]: IGroupedRights<IPropertyRightsetDocumentRelationshipPair> };
	templateMetaData: { [templateID: string]: ICharacteristicMetaDataCollection };
	facetResults: { [key: string]: Array<ISearchFacet> };
}

//#endregion

//#region Assets

export interface IMediaItem {
	fileName: string;
	filePath: string;
	mediaTypeID: number;
	mediaTypeLabel: string;
	mimeType: string;
	mediaTypeExtensions: string;
	contentType: string;
	mediaUrl: string;
	downloadUrl: string;
}

export interface IAssetMediaItem extends IMediaItem {
	mediaUrl: string;
	downloadUrl: string;
}

export interface IMediaType {
	mediaTypeID: number;
	mediaTypeLabel: string;
	mimeType: string;
	securityIndicator: number;
	mediaTypeExtensions: string;
}

export interface IAssociationMediaRequest {
	assetID: number;
	mediaTypeID: number;
	key: string;
	filename: string;
}

export interface IExecuteAssetWorkflowResults {
	successAssetIDs: number[];
	errorAssetIDs: number[];
}

export interface IFilestackPickFile {
	filename: string;
	handle: string;
	mimetype: string;
	originalPath: string;
	size: number;
	source: string;
	url: string;
	key: string;
	container: string;
	status: "Stored" | "InTransit" | "Failed";
}

export interface IFilestackPickFileWithEntityID {
	pickedFiles: IFilestackPickFile[];
	entityID: string;
}


export interface IKeyArtResponse {
	src: string;
}
//#endregion

//#region Financials

export interface IFinancialDocumentsCollection {
	recordDocuments: IRecordDocument[];
	documentUrls: { [key: string]: string };
}

//#endregion

//#region Site

export interface ILayoutState {
	compactMenu: boolean;
	isSidePanelOpen: boolean;
}

export interface RecentActivityItem {
	entityID: string;
	entityTitle: string;
	userID: number;
	userName: string;
	email: string;
	statusID: number;
	status: string;
	timestamp: string;
	localTimestamp: string;
	created: string;
	lastUpdated: string;
	statusUpdated: string;
	charTypeID: number;
	recordID: number;
	recordRevisionID: number;
	templateGroupID: number;
	subTitle: string;
}

export interface UserNotification {
	id: number;
	entityID: string;
	entityTitle: string;
	recordID: number;
	recordRevisionID: number;
	subject: string;
	body: string;
	attachment: string;
	toID: number;
	toEmail: string;
	fromID: number;
	fromEmail: string;
	createdAt: string;
	status: string;
	markedReadAt: string;
}

export interface IFancyTreeNode {
	title: string;
	key: number;
	children: Array<IFancyTreeNode>;
}

export interface IFavoriteLink {
	name: string;
	url: string;
}
//#endregion 


export interface ICharDataExtensionDataDateMathCalc {
	dateMathCalcID: number;
	divID: number;
	charTypeID: number;
	recID: number;
	recCharID: number;
	dateMathCalcJson: string;
	timeFrameID: number;
	frequencyIntervalID: number;
	frequencyRelativeIntervalID: number;
	dateRecalcOptionID: number;
	isCalc: boolean;
	compareToDate: string;
	calcConditionID: number;
	dateOptionID: number;
}

export interface ICharDataExtensionDataDateMathRel {
	dateMathRelID: number;
	divID: number;
	recCharID: number;
	parentRecCharID: number;
	parentCharTypeID: number;
	parentRecID: number;
	dateMathCalcID: number;
	isPriorTo: boolean;
	yearQuantity: number;
	monthQuantity: number;
	weekQuantity: number;
	dayQuantity: number;
	frequencyID: number;
	relativeCatalogID?: number;
	sortOrder: number;
}

export interface ICharDataExtensionDataDateMathChildRelInfo {
	entityID: string;
	path: string;
	currentValue: string;
	isCalculated: boolean;
	recalcOptionID: number;
	title: string;
	charLabel: string;
}

export interface ICharDataExtensionDataDateMathParentRelInfo {
	dateMathRelID: number;
	entityID: string;
	path: string;
	currentValue: string;
}

export interface ICharDataExtensionDataDateMath {
	calcsChangeTracking: { [recCharID: number]: -1 | 0 | 1 }; // -1 deleted, 0 created, 1 updated
	calcs: ICharDataExtensionDataDateMathCalc[];
	relationshipChangeTracking: { [dateMathRelID: number]: -1 | 0 | 1 }; // -1 deleted, 0 created, 1 updated
	childRelationships: ICharDataExtensionDataDateMathRel[];
	relationships: ICharDataExtensionDataDateMathRel[];
}

export interface ICharDataExtensionData {
	dateMath?: ICharDataExtensionDataDateMath;
	// alerts go here
}

//#region Entity
export interface ICharDataValue {
	_hash?: number;
	charID: number;
	recCharID: number;
	value: string;
	valueID?: number;
	ext?: ICharDataExtensionData;
}

export interface ICharDatas {
	[tagLabel: string]: ICharDataValue[];
}

export interface IEntityCharDataPair {
	record: IEntity;
	charDataCollection: ICharDataCollection;
}

export interface IEntitySearchDoc {
	entityID?: string;
	charTypeID?: number;
	divisionID?: number;
	recordID?: number;
	title?: string;
	statusID?: number;
	status?: string;
	templateID?: number;
	template?: string;
	templateGroupID?: number;
	templateGroup?: string;
	recordRevisionID?: number;
	lastUpdatedBy?: number;
	lastUpdated?: string;
	statusUpdatedBy?: number;
	statusUpdated?: string;
	createdBy?: number;
	created?: string;
	activeIndicator: number;
	lockedIndicator: number;
	deleteIndicator: number;
	characteristics: { [tagLabel: string]: any };
}

export interface IEntity {
	id: string;
	charTypeID: number;
	divisionID: number;
	recordID: number;
	title: string;
	statusID: number;
	status: string;
	templateID: number;
	template: string;
	templateGroupID: number;
	templateGroup: string;
	recordRevisionID: number;
	lastUpdatedBy: number;
	lastUpdated: string;
	statusUpdatedBy: number;
	statusUpdated: string;
	createdBy: number;
	created: string;
}

export interface ICharDataCollection {
	entityID: string;
	status: IEntityStatus;
	templateMetaData: ICharacteristicMetaDataCollection;
	charDatas: ICharDatas;
}

export interface IEntityStatus {
	activeIndicator: number;
	stepID: number;
	processID: number;
	divisionID: number;
	stepName: string;
	deleteIndicator: number;
	stepDescription: string;
	lockIndicator: number;
	sequenceNumber: number;
	createdBy: number;
	createdAt: string;
	updatedBy: number;
	updatedAt: string;
}

export interface ICharacteristicMetaDataCollection {
	divID: number;
	charTypeID: number;
	templateID: number;
	groups: Array<ITemplateCharacteristicGroup>;
	template: ICharacteristicTemplate;
	characteristicMetaDatas: Array<ICharacteristicMetaData>;
}

export interface ITemplateCharacteristicGroup {
	groupID: number;
	groupLabel: string;
	systemIndicator: number;
	sequenceNumber: number;
	expGroupIndicator: number;
	acl: string;
}

export interface ICharacteristicTemplate {
	charTypeID: number;
	templateID: number;
	templateGroup: string;
	templateGroupID: number;
	workflowProcessID: number;
	templateName: string;
	description: string;
	systemIndicator: number;
	visibilityIndicator: number;
	sequenceNumber: number;
	acl: string;
	visualization: string;
	calcSequence: number;
	dealScoped: number;
}

export interface ITemplateAssociation {
	divID: number;
	childCharTypeID: number;
	childCharTypeLabel: string;
	childTemplateID: number;
	childTemplateLabel: string;
	relRecTypeID: number;
	relRecTypeDescription: string;
	selected: boolean;
}

export interface ITemplateAssociationCollection {
	divID: number;
	charTypeID: number;
	templateID: number;
	charTypeAssociationList: ICharacteristicTypeAssociation[];
	templateAssociationList: ITemplateAssociation[];
}

export interface ICharacteristicTypeAssociation {
	charTypeDescription: string;
	relRecTypeID: number;
	charTypeID: number;
}

export interface IDataDefaultSet {
	id: string;
	label: string;
	hasConfigurationError: boolean;
	templateIDs: number[];
}

export interface ITemplateDefaults {
	associatedTemplates: ICharacteristicTemplate[];
	dataDefaultSets: IDataDefaultSet[];
}

export interface ITemplateCharacteristicMetaData {
	templateIDs: number[];
	characteristicMetaData: ICharacteristicMetaData;
}

export interface ICharacteristicMetaData extends ICharacteristic {
	groupID: number;
	sequenceNumber: number;
	requiredIndicator: number;
	disableControlValidation?: boolean;
	charValueSourceID: string;
	acl: string;
	triggerTypeID: string;
	triggerCharID?: number;
	triggerValue: string;
}

export interface ICharacteristicMetaDataLegacyIndicators extends ICharacteristicMetaData {
	visibilityIndicator: number;
	editIndicator: number;
	deleteIndicator: number;
}

export interface ICharacteristic {
	charTypeID: number;
	characteristicID: number;
	label: string;
	tagLabel: string;
	maxLength: number;
	dataTypeID: number;
	systemIndicator: number;
	reportIndicator: number;
	multipleIndicator: number;
	charValueSetID: string;
	description: string;
}

export interface ICharacteristicMetaDataValue {
	characteristicValueID: number;
	characteristicValueLabel: string;
	defaultIndicator: number;
	sequenceNumber: number;
	childValues: Array<ICharacteristicMetaDataValue>;
}

export interface ICharLovMetaData {
	charValueSourceID: string;
	charValueSetID: string;
	listOfValues?: Array<ICharacteristicMetaDataValue>;
	charValueGroups?: Array<ICharacteristicValueGroup>;
	charValueGroupAssociations?: { [key: number]: string[] };
}

export interface IDynamicReportLOV {
	recID: number;
	col2: string;
	col3: string;
	col4: string;
	col5: string;
}

export interface ITransactionJobRelationshipPair extends IEntityRelationshipPair {
	inventoryItems: IEntityRelationshipPair[];
}

export interface IAssociationCharTypeAndTemplateGroup {
	name: string;
	createName: string;
	charTypeID: number;
	tgIDs: number[];
}

export interface IAssociationCharTypeAndTemplateGroupExtended {
	name: string;
	createName: string;
	charTypeID: number;
	tgIDs: number[];
	isParentChar: boolean;
}

export interface IAssociationCharTypeTemplates {
	name: string;
	id: number;
	children?: IAssociationCharTypeTemplates[];
	isParentChar?: boolean;
	recordCount?: number;
}

export interface ICharTypeTemplateAssociationSelection {
	isParentChar: boolean;
	charTypeID: number;
	templateID?: number;
	associationID?: number;
	selected: boolean;
}

export interface IUpdateEntityTemplateEntityCheck {
	divID?: number;
	charTypeID?: number;
	recordID?: number;
	charID?: number;
	charLabel: string;
	charValueID?: number;
}

export interface IUpdateEntityTemplateRelationshipsCheck {
	divID?: number;
	charTypeID?: number;
	recordID?: number;
	childCharTypeID?: number;
	childRecordID?: number;
	relRecTypeID?: number;
	relRecIndicatorID?: number;
	relationshipID?: number;
	relationshipLevel?: number;
}

export interface IEntityRelationshipPair extends IEntityCharDataPair {
	relationship: IEntityRelationship;
}

export interface IDocumentRelationshipPair {
	document: IEntitySearchDoc;
	relationship: IEntityRelationship;
}

export interface IRecordRelationshipPair {
	record: IEntity;
	relationship: IEntityRelationship;
}


export interface IRecordRelationshipPairChild {
	record: IEntity;
	relationship: IEntityRelationshipChild;
}

export interface IRecordRelationshipSubChildren {
	record: IEntity;
	relationship: IEntityRelationshipChild;
	children: IRecordRelationshipSubChildren[];
}

export interface IEntityRelationship {
	divisionID: number;
	parentRecID: number;
	parentCharTypeID: number;
	childCharTypeID: number;
	childRecID: number;
	level?: number;
	relRecID?: number;
	relTypeID?: number;
	relIndicatorID?: number;
	sequenceNumber: number;
}

export interface IEntityRelationshipChild {
	divisionID: number;
	parentRecID: number;
	parentCharTypeID: number;
	childCharTypeID: number;
	childRecID: number;
	level: number;
	relRecID: number;
	relTypeID: number;
	relIndicatorID: number;
	numChildRecords: number;
}

export interface IEntityID {
	recID: number;
	charTypeID: number;
	divID: number;
}

export interface IRemovedComponentTriple {
	parentEntityID: string;
	removedComponentID: string;
	catalogItemID: string;
	catalogItemTitle: string;
}

export interface IWFGetStatusForEntityRequest {
	entityID: IEntityID;
}

export interface TemplateEntityID {
	templateID: number;
	charTypeID: number;
	divID: number;
}

export interface IPropertyRightsetDocumentRelationshipPair extends IDocumentRelationshipPair {
	transactionID: IEntityID;
	propertyID: IEntityID;
	contactRels: Array<IEntityRelationship>
}

export interface ICreateEntityResponse {
	success: boolean;
	entityID: string;
}

export interface ICreateEntityRelationshipsResponse {
	success: boolean;
	relationships: string[];
}

export interface IRelationshipIDResponse {
	relRecID: string;
}

export interface ICreateAssetRelationResponse {
	relationshipIDs: string[];
}

export interface IEntityNode {
	entity: IEntity;
	charDataCollection: ICharDataCollection;
	relationships: IEntityRelationship[];
	relatedEntities: { [key: string]: IEntityNode };
}

export interface IEntityNodeWRelID extends IEntityNode {
	relID: string;
}

export interface IValidationResult {
	isValid: boolean;
	isLocked: boolean;
	isDeletable: boolean;
	errors: IKeyValuePair<string, string[]>[];
	dbValidationErrors: IDbValidationError[];
}

export interface IDbValidationError {
	errorType: string;
	value: string;
	entityId: string;
	entityTitle: string;
	entityTemplate: string;
	charId: number;
	charLabel: string;
	charTagLabel: string;
	childCharTypeId?: number;
	childEntityId: string;
	childEntityTitle: string;
	childEntityTemplate: string;
	relationshipLabel: string;
	errorBlocker: boolean;
}

export interface ICharDataMoneyValue {
	locAmt?: string;
	locCur?: number;
	locSym?: string;
	divAmt?: string;
	divCur?: number;
	divSym?: string;
}

export interface IBulkEntityRelationships {
	parentEntityID: string;
	entityCharData: IBulkEntityCharData;
	templateID: number;
	relationships: { [charTypeID: number]: number[] };
}

export interface IBulkEntityCharData {
	entityID: string;
	charDatas: ICharDatas;
}
//#endregion

//#region Entity Config

export interface ICharacteristicType {
	charTypeID: number;
	charTypeName: string;
	seqNumber: number;
}

export interface ICharacteristicTemplateGroup {
	characteristicTemplateGroupID: number;
	characteristicTemplateGroupName: string;
	sequenceNumber?: number;
}

export interface ICharEditResponse {
	charID: number;
	success: boolean;
	message: string;
}

//#endregion

//#region Alerts

export interface IAlert {
	divisionID: number;
	charTypeID: number;
	recordID: number;
	ID: string;
	label: string;
	fromID: number;
	toEmail: string;
	subject: string;
	body: string;
	attachment: string;
	sequenceNumber: number;
	createdBy: number;
	createdAt: string;
	updatedBy: number;
	updatedAt: string;
	status: string;

	//New properties for MVCA-183
	charID: number;
	templateID: number;
	processID: number;
	messageID: number;
	bodyHTML: string;
	quantity: number;
	frequencyID: number;
	frequencyLabel: string;
	timeframeID: number;
	timeframeLabel: string;
	selectedRoleIDs: string;
	selectedPartyIDs: string;

	//New properties for Date Math
	alertWhenSetOrUpdated: boolean;
	createCustomAlert: boolean;

	//New properties for MVCA-2816
	createEmailInvite: boolean;
	emailInviteUID: string;
	emailUpdateSequence: number;

	//New properties for MVCA-5568
	templateDateCharId: number;
	templateDateContainsType: number;

	//New property for RL-7463
	doNotSendIfRecordInactive: boolean;

	//New property for API-167
	triggerSNSOrSQS: boolean;

	notifyChildrenPartyIds: string;
}

export interface IAlertFrequency {
	frequencyID: number;
	frequencyLabel: string;
}

export interface IAlertTimeframe {
	timeframeID: number;
	timeframeLabel: string;
}

export interface IAlertContainsType {
	label: string;
	value: number;
}

//#endregion Alerts

//#region Date Calculations



export interface IDateCalcTimeframe {
	timeframeID: number;
	timeframeLabel: string;
}

export interface IDateCalcFrequency {
	frequencyID: number;
	frequencyLabel: string;
}

export interface IDateCalcFrequencyRelative {
	frequencyRelativeID: number;
	frequencyRelativeLabel: string;
}

export interface IDateCalcFrequencyInterval {
	frequencyIDateCalcFrequencyIntervalID: number;
	frequencyIDateCalcFrequencyIntervalLabel: string;
}

export interface IDateCalcRecalcOption {
	recalcOptionID: number;
	recalcOptionLabel: string;
}

export interface IDateCalcCondition {
	dateCalcConditionID: number;
	dateCalcConditionLabel: string;
}

export interface IDateCalcDateOption {
	dateCalcDateOptionID: number;
	dateCalcDateOptionLabel: string;
}

export interface ICorrectRelativeToDateMath {
	assetId: number;
	recCharId: number;
	tagLabel: string;
}

export interface IParentEntityIDAndTitle {
	entityID: string;
	title: string;
	entityString: string;
}

export interface IEntityIdAndTitle {
	entityId: string;
	title: string;
}

export interface IGetDateMathRelatedEntityPairsResponse {
	entities: IEntityRelationshipPair[],
	numFound: number;
	tableRecIDsWithAmounts: number[];
	catalogItemPipeList: { [relid: number]: string } | {};
	contactPipeList: { [relid: number]: string } | {};
	projectPipeList: { [relid: number]: string } | {};
};

//#endregion Date Calculations

//#region Workflow

export interface IWFNewStatus {
	statusID: number;
}

export interface IWFAction {
	processID: number;
	divisionID: number;
	actionID: number;
	actionName: string;
	actionDescription: string;
	pathID: number;
	pointProcID: number;
	nextPageID: number;
	sequenceNumber: number;
	createdBy: number;
	createdAt: string;
	updatedBy: number;
	updatedAt: string;
	workflowActionUrl: string;
	docGenIndicator: number;
}

export interface IWFActionSaveRequest {
	processID: number;
	divisionID: number;
	actionID: number;
	actionName: string;
	actionDescription: string;
	pathID: number;
	pointProcID: number;
	nextPageID: number;
	sequenceNumber: number;
	updatedBy: number;
	selectedRoleIDs: number[];
	selectedPartyIDs: number[];
	selectedStepIDs: { [key: number]: number; };
	selectedChainedActionIDs: { [key: number]: number; };
	selectedNotificationIDs: { [key: number]: number; };
	selectedPartyNotifications: IPartyWithNotification[];
	docGenIndicator: number;
}

export interface IWFActionConfig extends IWFAction {
	nextStep: string[];
	nextAction: string[];
	updateProcess: string[];
	selectedPath: IWFActionPathConfig;

	allRoles: IWFActionRole[];
	allSteps: IWFStep[];
	allProcesses: IWFProcess[];

	selectedSteps: { [processID: number]: number };
	selectedChainedActions: { [processID: number]: number };
	selectedProcessIDs: number[];

	selectedRoles: IWFActionRole[];
	selectedNotificationRoleIDs: number[];

	selectedParties: IParty[];
	selectedNotificationPartyIDs: number[];

	selectedRoleNotifications: { [roleID: number]: IWFRoleNotification };
	selectedPartyNotifications: IPartyNotification[];

	docIndicator: number;
}

export interface IPartyNotification {
	partyID: number;
	notificationID: number;
	notifyChildren: boolean;
}

export interface IWFNotificationRole {
	roleID: number;
	notificationID: number;
}

export interface IWFSendNotificationsRequest {
	charTypeID: number;
	recordID: number;
	processID: number;
	actionID: number;
	notifications: IWFNotificationWithAttachments[];
	downloadURLs: { [key: number]: string };
}

export interface IWFSendNotificationRequest {
	charTypeID: number;
	recordID: number;
	actionID: number;
	processID: number;

	messageID: number;
	notificationSubject: string;
	plainText: string;
	htmlText: string;
	attachments: string[];
	uploadedDocuments: { [key: string]: string }; //entityIDs
	batchID: string;
	mergeFields?: IEntityMergeField[];
}

export interface IWFPreviewNotifications {
	notifications: IWFNotificationWithAttachments[];
}
export interface IWFNotificationWithAttachments extends IWFRoleNotification {
	attachments: ISelectedNotifications[];
	uploadedDocuments: string[];
}
export interface ISelectedNotifications {
	selected: boolean;
	attachment: string;
}
export interface IWFNotificationWithFlags {
	notification: IWFRoleNotification;
	previewBool: boolean;
}

export interface IWFDocumentTemplateType {
	charTypeID: number;
	templateTypeDescription: string;
	sequenceNumber: number;
}

export interface IWFEntityStatusUpdateRequest {
	charTypeID: number;
	recID: number;
	statusID: number;
}

export interface IExecuteWorkflowResult {
	success: boolean;
	newStatusID: number;
	errorStep: string;
	message: string;
}

export interface IAddNotificationsToQueueResult {
	notificationsAddedToQueue: number;
}

export interface ICreateBatchIDResult {
	batchID: string;
}

export interface IWFProcess {
	processID: number;
	divisionID: number;
	charTypeID: number;
	processName: string;
	processDescription: string;
	sequenceNumber: number;
	createdBy: number;
	createdAt: string;
	updatedBy: number;
	updatedAt: string;
}

export interface IWFStepAssoc {
	divisionID: number;
	processID: number;
	stepID: number;
	lockIndicator: number;
	deleteIndicator: number;
}

export interface IWFStepConfig extends IWFStepAssoc {
	stepName: string;
	processName: string;
}

export interface IWFProcessConfig extends IWFProcess {
	steps: IWFStepConfig[];
}

export interface IWFStep {
	stepID: number;
	processID: number;
	divisionID: number;
	stepName: string;
	stepDescription: string;
	lockIndicator: boolean;
	activeIndicator: number;
	deleteIndicator: boolean;
	sequenceNumber: number;
	systemIndicator: number;
	createdBy: number;
	createdAt: string;
	updatedBy: number;
	updatedAt: string;
}

export interface IWFStepSaveRequest {
	stepID: number;
	createdBy: number;
	divID: number;
	processID: number;
	stepDescription: string;
	stepName: string;
	lockIndicator: boolean;
	activeIndicator: number;
	deleteIndicator: boolean;
	stepRoleLabels: { [key: number]: string };
	selectedActionIDs: number[];
	sequenceNumber: number;
	systemIndicator: number;
}

export interface IWFStepRoleAssocRequest {
	associations: IWFStepRoleAssoc[];
	processID: number;
	roleID: number;
}

export interface IWFActionRoleAssocRequest {
	associations: IWFActionRoleAssoc[];
	processID: number;
	roleID: number;
}

export interface IWFStepRoleAssoc {
	divisionID: number;
	processID: number;
	roleID: number;
	stepID: number;
	stepName: string;
}

export interface IWFRoleActionAssoc {
	divisionID: number;
	processID: number;
	roleID: number;
	actionID: number;
}

export interface IWFAssocAction {
	divisionID: number;
	processID: number;
	actionID: number;
}

export interface IWFActionPathConfig {
	actionPathID: number;
	actionPathName: string;
	actionPathDescription: string;
	sequenceNumber: number;
}

export interface IWFActionRole {
	divisionID: number;
	roleID: number;
	roleName: string;
	roleDescription: string;
	superUserIndicator: boolean;
	sequenceNumber: number;
	createdBy: number;
	createdAt: string;
	updatedBy: number;
	updatedAt: string;
	memberCount: number;
}

export interface IWFActionRoleConfig extends IWFActionRole {
	notification: IWFRoleNotification;
}

export interface IWFRoleNotification {
	divisionID: number;
	processID: number;
	id: number;
	label: string;
	description: string;
	subject: string;
	body: string;
	bodyHTML: string;
	sequenceNumber: number;
	documentTypes: number[];
	createdBy: number;
	createdAt: string;
	updatedBy: number;
	updatedAt: string;
	previewIndicator: number;
	addActionBtn: boolean;
	triggerSNSOrSQS: boolean;
}


export interface IWFActionRoleAssoc {
	actionID: number;
	divisionID: number;
	processID: number;
	roleID: number;
}

export interface IDocTemplate {
	documentTemplateID: number;
	templateLabel: string;
	templateDescription: string;
	fileName: string;
}

export interface IDocTemplateSaveRequest {
	doc: IDocTemplate;
	charTypeID: number;
	selectedTemplateIDs: number[];
}

export interface IDocTemplateConfig {
	doc: IDocTemplate;
	downloadURL: string;
}

export interface IDocumentSearchDoc {
	title: string;
	status: string;
	template: string;
}

export interface IDocTemplateValidationResult {
	valid: boolean;
	errors: string[];
}

export interface IDocTemplateValidationRequest {
	fileName: string
}

export interface ICharTemplate {
	templateID: number;
	templateName: string;
}

export interface IDocTemplateRequest {
	charTypeID: number;
	docTemplateID: number;
}

export interface IDocTemplateByCharTemplateRequest {
	charTypeID: number;
	charTemplateID: number;
}

export interface IDocGenTemplate {
	id: number;
	name: string;
	checked: boolean;
}
export interface IDocGenAttachmentRequest {
	charTypeID: number;
	documentTemplateID: number;
	path: string;
	fileName: string;
}

export interface IDocGenRequest {
	documentTemplateID: number;
	actionID: number;
	processID: number;
	characteristicTypeID: number;
	recordID: number;
	requestRevisionID: number;
	formatType: number;
	templateFileName: string;
	statusID: number;
}
export interface ISignedUrl {
	url: string;
}

export interface IDocGenResponse {
	success: boolean;
	fileName: string;
}

export interface IStatusHistoryItem {
	date: string;
	status: string;
	duration: number;
	user: string;
	avatar: string
}
//#endregion

//#region Reporting

export interface IPropertyAvailability {
	territory: string;
	parentTerritory: string;
	territoryID: number;
	parentTerritoryID?: number;
	level: number;
	startDate: string;
	endDate: string;
	exclusive?: number;
	media: string;
	leafNode: number;
	rightsCode: number;
}

export interface IPropertyAvailability2 {
	propertyID: number;
	propertyTitle: string;
	propertyTypeLabel: string;
	propertyCharLabel: string;
	startDate: string;
	endDate: string;
	exclusive: string;
	territory: string;
	media: string;
	language: string;
	channel: string;
}

export interface IGetAvailsWindowsRow {
	assetID: number;
	assetTitle: string;
	assetTemplate: string;
	characteristics: _.Dictionary<any>;
	windowStart: string;
	windowEnd: string;
	dim1BranchValues: string[];
	dim2BranchValues: string[];
	dim3BranchValues: string[];
	dim4BranchValues: string[];
	isExclusive: boolean;
	isTupleAvailable: boolean;
	directParents: IDictionaryN<string>;
	topParents: IDictionaryN<string>;
}

export interface IGetAvailsWindowsResults {
	rows: IGetAvailsWindowsRow[];
	total: number;
}

export interface IGetAvailsWindowAssetsRow {
	assetID: number;
	title: string;
	status: string;
	template: string;
	characteristics: { [key: string]: any };
	directParents: IDictionaryN<string>;
	topParents: IDictionaryN<string>;
}

export interface IGetAvailsWindowAssetsResults {
	rows: IGetAvailsWindowAssetsRow[];
	total: number;
}


export interface IEntityAttachmentRequest {
	charTypeID: number;
	recID: number;
	path: string;
	name: string;
	description?: string;
	attachmentTypeID?: number;
	makeKeyArt: boolean;
	makeThumb: boolean;
}


export interface IAttachment {
	fileName: string;
	filePath: string;
	attachmentID: number;
	label: string;
	mimeType: string;
	mediaTypeExtensions: string;
	contentType: string;
}

export interface IAssetAttachment extends IAttachment {
	downloadUrl: string;
	mediaUrl: string;
	previewType: string;
	videoSrc: string;
	attachmentTypeID: number;
}

export interface ISavedQueryReport {
	reportName: string;
	charID: number;
	templateID: number;
	relatedCharID: number;
	relatedTemplateID: number;
	columns: IDynamicReportColumnChar[];
	relIndId: number;
}

export interface IBackgroundReportResults<T> {
	status: 'done' | 'queued' | 'processing' | 'error';
	message: string;
	data: T;
}

export interface IAvailSearchDetailResponse {
	rightSetID: number;
	dealID: number;
	dealTitle: string;
	dealStatus: string;
	dealTemplate: string;
	dealCreated: string;
	dealUpdated: string;

	parties: string[];

	rightsData: ICharDataCollection;
}

export interface IPropertyCoverageWindow {
	startDate: string;
	endDate: string;
	windowCounts: IPropertyCoverageWindowCount[];
}

export interface IPropertyCoverageWindowMoment {
	startDate: moment.Moment;
	endDate: moment.Moment;
	windowCounts: IPropertyCoverageWindowCount[];
}

export interface IPropertyCoverageWindowCount {
	valueID: number;
	value: string;
	count: number;
}

//#endregion

//#region Admin

export interface IEntityCharTypes {
	charTypeID: number;
	charTypeName: string;
	seqNumber: number;
}

export interface IEntityCharTypeTemplate {
	templateID: number;
	templateGroupID: number;
	workflowProcessID: number;
	templateName: string;
	description: string;
	systemIndicator: number;
	//visibilityIndicator: number;
	sequenceNumber: number;
	templateGroup: number;
}

export interface IEntityTemplateTypeStatus {
	StepID: number;
	ProcessID: number;
	DivisionID: number;
	StepName: string;
	StepDescription: string;
	LockIndicator: number;
	ActiveIndicator: number;
	DeleteIndicator: number;
	SequenceNumber: number;
	CreatedBy: number;
	CreatedAt: string;
	UpdatedBy: number;
	UpdatedAt: string;
}

export interface IEntityIndexInfo {
	charTypes: ICharacteristicType[];
	divisions: IDivisionInfo[];
}

export interface IDivisionInfo {
	divID: number;
	divisionDescription: string;
}


export interface IAttachmentRequest {
	EntityID: string;
	AttachmentTypeID: number;
	Label: string;
	Name: string;
	Path: string;
	MakeKeyArt: boolean;
	MakeThumb: boolean;
}

export interface IRebuildIndexRequest {
	charTypeIDs: number[];
	divisionIDs: number[];
	entityIDs: string[];
	clearMatchingDocs: boolean;
	templateID?: number;
	offsetRecID?: number;
}

export interface IZipRebuildIndexRequest {
	charTypeIDs: number[];
	divisionIDs: number[];
	offsetRecID?: number;
}

export interface IRebuildEntitiesStatusIndexRequest {
	divisionID: number;
	processID: number;
	statusID: number;
}

export interface IRebuildEntitiesTemplateIndexRequest {
	divisionID: number;
	charTypeID: number;
	templateID: number;
}

export interface IJobResponse {
	jobID: string;
	jobTitle?: string;
}

export interface IJobMessage {
	timestamp: string;
	id: string;
	message: string;
}

export interface IJobState {
	jobID: string;
	status: string;
	statusDescription: string;
	total: number;
	completed: number;
	failed: number;
	infoMessages: IJobMessage[];
	createdAt: string;
	progressMessage: string;
	resultMessage: string;
	progressResultData: string;
}

export interface IJobMessageList {
	totalCount: number;
	start: number;
	messages: IJobMessage[];
}

export interface IBenchmarkResult {
	results: string;
}

//#endregion

//#region Security

export interface ISecurityTreeNode {
	name: string;
	key: string;
	icon: string;
	description: string;
	securityActivityID: string;
	charTypeID: number;
	templateGroupID: number;
	templateID: number;
	charGroupID: number;
	charID: number;
	childNodes: ISecurityTreeNode[];
}

export interface ISecurityTreeRow {
	parentTreeRow: ISecurityTreeRow;
	childTreeRows: ISecurityTreeRow[];
	rowKey: string;
	node: ISecurityTreeNode;
	level: number;
	open: boolean;
}

export interface IPermissionSet {
	[key: string]: number
}

export interface ISecurityRight {
	name: string;
	rightFlag: number;
}

export interface IGraphNode<T> {
	uniq: string;
	item: T;
	childNodes: IGraphNode<T>[];
	//parentNode: IGraphNode<T>;
	//AddChildValue: (value: T) => void;
	//AddChildNode: (node: IGraphNode<T>) => void;
}

export class GraphNode<T> implements IGraphNode<T> {

	uniq: string;
	item: T;
	childNodes: IGraphNode<T>[];
	constructor(item: T, childValues: T[] = null) {
		this.uniq = uuid.v4();
		this.item = item;
		this.childNodes = (childValues || []).map((item) => new GraphNode<T>(item, null));
	}
}

export interface IFileStackSignatureResponse {
	signature: string;
}

export interface ISecurityAccessResponse {
	accessResult: number;
}

export interface IAspNetSecurityPolicy {
	securityPolicyID: string;
	divID: number;
	policyTitle: string;
	policyType: number;
	isGlobal: boolean;
}

export interface IAspNetSecurityActivity {
	securityActivityID: string;
	parentSecurityActivityID: string;
	activityKey: string;
	activityTitle: string;
	activityDescription: string;
	sequenceNumber: number;
	childActivities: IAspNetSecurityActivity[];
}

export interface IAspNetSecurityPolicyPermission {
	securityPolicyPermissionID: string;
	securityPolicyID: string;
	securityActivityID?: string;
	charTypeID?: number;
	templateGroupID?: number;
	templateID?: number;
	charGroupID?: number;
	charID?: number;
	permissionValue: number;
}

export interface ICreatedGuidResponse {
	id: string;
}

export interface ICharacteristicPolicySummary {
	policies: IAspNetSecurityPolicy[];
	permissions: _.Dictionary<IAspNetSecurityPolicyPermission[]>;
}

//#endregion

//#region Hierarchy
export interface IHierarchyRequest {
	entityID: string;
	templateGroupIDs: string;
}

export interface ICharacteristicValueSet {
	divID: number;
	charValueSetID: string;
	charValueSetLabel: string;
	charValueSetDescription: string;
	createdBy: number;
	createdAt: string;
	updatedBy: number;
	updatedAt: string;
}

export interface ICharacteristicValueSource {
	divID: number;
	charValueSourceID: string;
	charValueSetID: string;
	charValueSourceLabel: string;
	charValueSourceDescription: string;
	createdBy: number;
	createdAt: string;
	updatedBy: number;
	updatedAt: string;
}

export interface ICreateCharValueSetResponse {
	charValueSetID: string;
	charValueID: number;
}

export interface ICharacteristicValue {
	divID: number;
	charValueSetID: string;
	charValueID: number;
	charValueLabel: string;
	charValueDescription: string;
	xref: string;
	statusID: number;
}

export interface ICharacteristicValueHierarchy {
	divID: number;
	charValueSourceID: string;
	charValueHierarchyID: string;
	charValueID: number;
	parentCharValueID: number;
	level: number;
	childCount: number;
	minPos: number;
	maxPos: number;
}

export interface ICharacteristicValueGroup {
	charValueGroupID: string;
	charValueSourceID: string;
	divID: number;
	charValueGroupLabel: string;
	charValueGroupDescription: string;
	charValueAssociations: Array<ICharacteristicValueGroupCharacteristicValueAssociation>
}

export interface ICharacteristicValueGroupCharacteristicValueAssociation {
	charValueGroupCharValueID: string;
	divID: number;
	charValueGroupID: string;
	charValueID: number;
}

export interface ICreateCharValueGroupResponse {
	charValueGroupID: string;
}

export interface ICharacteristicValueHierarchyValueGroupAssociation {
	divID: number;
	charValueSourceID: string;
	charValueHierarchyID: string;
	charValueGroupID: string;
	createdBy: number;
	createdAt: string;
	lastUpdatedBy: number;
	lastUpdated: string;
}
//#endregion

//#region Rights
export interface IRightSetBulkEditResponse {
	message: string;
	bulkMessage: number;
	jobID: string;
	success: boolean;
}
//#endregion

//#region TreeList2
export interface ITreeList2Item {

}
//#endregion

//#region Royalty Statement
export interface IExportRoyaltyStatementResponse {
	success: boolean;
}

export interface IGetRoyaltyStatementsRequest {
	endDate: string;
	startDate: string;
	docTypeIDs: number[];
	incomeGroupIDs: number[];
	contactID?: number;
	companyID: number;
	exportType?: number;
	exportView?: number;
	exportDocType?: number;
}

export interface IGetRoyaltyStatement {
	invoiceID: number;
	contact: IEntity;
	billingDocumentCount: number;
}

export interface IGetRoyaltyStatementsResponse {
	billingContacts: IGetRoyaltyStatement[];
}

export interface IRoyaltyStatement {
	amount: number;
	beginningBalance: number;
	billingCity: string;
	billingCountry: string;
	billingOrg: string;
	billingState: string;
	currency: string;
	description: string;
	firstPerformanceDate: Date;
	grossLicenseFee: number;
	incomeGroup: string;
	incomeGroupID: number;
	invoiceDueDate: Date;
	invoiceID: number;
	licenseDate: Date;
	properties: string[];
	remainingBalance: number;
	royaltyPercentage: number;
}

export interface IGetRoyaltyRecipientResponse {
	contact: {
		entity: IEntity;
		characteristics: ICharDatas;
	}
	royaltyStatementLines: IRoyaltyStatement[];
}
//#endregion

//#region AvailsExplorer
//export interface IAvailItem {
//    startDate: string;
//    dealID: number;
//    assetID: number;
//    endDate: string;
//}

//export interface IAvailSummary {
//    assetCount: number;
//    summaryWindows: IAvailSummaryWindow[];
//}

//export interface IAvailSummaryWindow {
//    startDate: Date;
//    endDate: Date;
//    availCount: number;
//    unAvailCount: number;
//    partialCount: number;
//}

//export interface IAvailAsset {
//    title: string;
//    assetID: number;
//    open: boolean;
//    dealCount: number;
//    deals: IAvailItem[][];
//    availWindows: IAvailAssetWindow[];
//}

//export interface IAvailAssetWindow {
//    startDate: Date;
//    endDate: Date;
//    status: number;
//}

export interface IAvailsTimelineSummaryWindow {
	windowStart: string;
	windowEnd: string;
	availableCount: number;
	partialCount: number;
	unavailableCount: number;
}

export interface IAvailsTimelineGroupWindow {
	windowStart: string;
	windowEnd: string;
	percentAvailable: number;
}

export interface IAvailsTimelineGroupRightset {
	rightsetID: number;
	dealID: number;
	templateID: number;
	templateGroupID: number;
	startDate: string;
	endDate: string;
	overrunStartDate?: string;
	isExclusive: boolean;
	isActive: boolean;
	templateVisualization: string;
	dim1ValueIDs: number[];
	dim2ValueIDs: number[];
	dim3ValueIDs: number[];
	dim4ValueIDs: number[];
	status: string;
}

export interface IAvailsTimelineAssetsRightsGroup {
	assetIDsHash: number;
	assets: { [key: number]: string };
	righsetIDs: number[];
	windows: IAvailsTimelineGroupWindow[];
	rightsets: IAvailsTimelineGroupRightset[];
}

export interface IAvailsTimelineResponse {
	clientNames: { [key: number]: string };
	dealTitles: { [key: number]: string };
	rightsTemplates: { [key: number]: string };
	assetRightsGroups: IAvailsTimelineAssetsRightsGroup[];
	summaryWindows: IAvailsTimelineSummaryWindow[];
	assetCount: number;
	rowCount: number;
}

//#endregion

//#region Account

export interface IAlertSetting {
	email: boolean;
}
export interface IDealSearchSetting {
	setting: string;
	contacts: string[];
}

export interface IGenerateGoogleAuthenticatorKeyResponse {
	secretKey: string;
	otpURL: string;
}

export interface IAccountManageState {
	userID: number;
	username: string;
	isGoogleAuthenticatorEnabled: boolean;
	profileAvatar: string;
	userDisplayName: string;
}

export interface IDataResponse<T> {
	success: boolean;
	message: string;
	data: T;
}

export interface IPagedDataResponse<T> {
	success: boolean;
	message: string;
	data: T;
	pageCount: number;
	numFound: number;
}
//#endregion

//#region Paging 
export interface IPagingOptions {
	rows: number;
	page: number;
	numFound?: number;
	rowCount?: number;
}
//#endregion

//#region Comments

export interface ICommentResponse {
	userId: number;
	comments: IComment[];
}

export interface IComment {
	parentId?: number;
	id?: number;
	userID: number;
	userName: string;
	profileAvatar: string;
	userEmail?: string;
	entityId?: string;
	text: string;
	created?: Date;
	updated?: Date;
	children?: IComment[];
	editMode: boolean;
}

//#endregion

//#region BulkUpload

export interface IBulkUploadValidateFileHeadersRequest {
	charTypeID: number;
	templateID: number;
	selectedFileName: string;
	mode: number;
}

export interface IBulkUploadValidationResult {
	characteristicID: number;
	recCharID: number;
	charValue: string;
	isValid: boolean;
	isHidden: boolean;
}

export interface IBulkUploadValidation {
	token: string;
	validationResult: { [key: number]: IBulkUploadValidationResult[] }
	errorOccured: boolean;
}

export interface IBulkUploadFileValidationResult {
	CharacteristicID: number;
	RecCharID: number;
	CharValue: string;
	IsValid: boolean;
	IsHidden: boolean;
}

export interface IBulkUploadFileValidation {
	Token: string;
	ValidationResult: { [key: number]: IBulkUploadFileValidationResult[] };
	ErrorOccured: boolean;
	PreValidationErrorMessage: { [key: number]: string };
}

export interface IBulkUploadValidateFileDataRequest extends IBulkUploadValidateFileHeadersRequest {
	assocCharTypeID: number;
	assocRelDirection: number;
	selectedHeaders: IBulkUploadValidationResult[];
	selectedDateFormat: string;
}

export interface ISavedBulkUploadHeaderMapping {
	mappingName: string;
	charTypeID: number;
	templateID: number;
	mappedHeaders: IBulkUploadValidationResult[];
}

export interface ISavedBulkUploadColumnLovMapping {
	mappingName: string;
	characteristicID: number;
	mappedLovs: { [key: string]: ICharacteristicValue },
	charValSourceID: string;
}
//#endregion

//#region UsageIn
export interface ICharValueSourceUsageIn {
	charValueSourceID: string;
	charTypeID: number;
	charTypeLbl: string;
	templateID: number;
	tempalteLbl: string;
	templateCharacteristicGroupID: number;
	templateCharacteristicGroupLbl: string;
	charateristicID: number;
	characteristicLbl: string;
}

export interface ICharValueSetUsageIn {
	charValueSetID: string;
	charTypeID: number;
	charTypeLbl: string;
	charateristicID: number;
	characteristicLbl: string;
}

export interface ICharacteristicUsageIn {
	templateID: number;
	tempalteLbl: string;
	templateCharacteristicGroupID: number;
	templateCharacteristicGroupLbl: string;
}
//#endregion

//#region Identity

export interface IAspNetUserRole {
	roleID: string;
	name: string;
	divID: number;
	activitySecurityPolicyID: string;
	dataSecurityPolicyID: string;
	workflowSecurityPolicyID: string;
	adminActivitySecurityPolicyID: string;
	createdAtDateUtc: string;
	updatedAtDateUtc: string;
	xref: string;
}

export interface IAspNetUser {
	Id: string;
	UserName: string;
	EmailConfirmed: boolean;
	PasswordHash: string;
	SecurityStamp: string;
	PhoneNumber: string;
	PhoneNumberConfirmed: boolean;
	TwoFactorEnabled: boolean;
	LockoutEndDateUtc: string;
	LockoutEnabled: boolean;
	AccessFailedCount: number;
	RLUserID: number;
	IsGoogleAuthenticatorEnabled: boolean;
	GoogleAuthenticatorSecretKey: string;
	ResetPasswordOnLogin: boolean;
	PasswordResetToken: string;
	PasswordResetDateUtc: string;
	ProfileAvatar: string;
	IsSuperUser: boolean;
	CreatedAtDateUtc: string;
	UpdatedAtDateUtc: string;
	charData: ICharDataCollection;
}

export interface IRLUser {
	entityID?: string;
	name: string;
	email: string;
	selectedAppUrls: IApplicationInfo[]
	selectedRoles: IAccountRole[];
	selectedIdentityRoles: IAspNetUserRole[];
	selectedTemplate: number;
}
//#endregion

//#region Association
export interface IAssociationsModel {
	parentRecID: number;
	parentCharTypeID: number;
}

export interface ICreateInventoryAssociationsModel extends IAssociationsModel {
	childCharTypeID: number;
	childRecIDs: number[];
	direction?: number;
}

export interface IDeleteInventoryAssociationsModel extends IAssociationsModel {
	relRecIDs: number[];
}
//#endregion

//#region AdminConfig
export interface ICurrencyConversionInfo {
	currencyConversionID: string;
	companyCurrency: string;
	transactionCurrency: string;
	rate: number;
	effectiveDate: string;
}

export interface IBulkUpdate {
	divID: number;
	charTypeID: number;
	recID: number;
	recRevID: number;
	staID: number;
	usrID: number;
}

export interface IDivSetting {
	divSettingID: string;
	key: string;
	value: string;
}

export interface IApiKey {
	divID: number;
	userID: number;
	rolID: number;
	privateKey: string;
	publicKey: string;
}

export interface MassUpdate {
	CharType: number;
	RecID: number;
	RecRevID: number;
	StaID: number;
}

export interface AmortizationModel {
	id: number;
	name: string;
	recurrence: string;
	numberOfPeriods: number;

	periods: AmortizationPeriod[];
}

export interface AmortizationPeriod {
	id: number;
	modelId: number;
	split: number;
}

//#endregion

export interface IQueryNode {
	[op: string]: Array<string | number | string[] | number[] | boolean | IQueryNode>;
}

//#region CurrencyConversion
export interface ICurrencyConversionResult {
	currencySymbol: string;
	currencyAmount: number;
}
//#endregion

//#region CatalogAvailsReport

export interface IGetCatalogAvailsWorksheetRowsResponse {
	rowCount: number;
	documents: IEntitySearchDoc[];
}

//#endregion

//#region Validation
export interface IValidateRecIDsOrTitlesResponse {
	recID: number;
	title: string;
	isValid: boolean;
	entityId: string;
}

export interface ICustomUserColumns {
	headerID?: number;
	charTypeID: number;
	name: string;
	isDefault: boolean;
	isGlobal: boolean;
	columns: ICustomUserColumn[];
}

export interface ICustomUserColumn {
	charTypeID: number;
	characteristicID?: number;
	solrLabel?: string;
	sequenceNumber: number;
	label?: string;
	formatter?: string;
	missingDefault?: boolean;
	hasLov?: boolean;
}
//#endregion

//#region List Table
export interface IListTableRow {
	recordID: number;
	recordRevisionID: number;
	columns: IListTableColumn[];
	title: string;
	currency: string;
	activeIndicator?: number;
	lockedIndicator?: boolean;
	deleteIndicator?: boolean;
	entityID?: string;
	templateGroupID?: number;
	templateID?: number;
	statusID?: number;
}

export interface IListTableColumn {
	label: string;
	value?: string;
	solrLabel: string;
	characteristicID: number;
	formatter?: string;
	dataTypeID: number;
	systemIndicatorID: number;
	multipleIndicator?: number;
}
//#endregion

//#region Usages

export interface IUsageTableCollection {
	assetTitles: { [recID: number]: string };
	projectTitles: { [recID: number]: string };
	contactTitles: { [recID: number]: string };
	mockTitles: { [recID: number]: string };
	usageTableGroups: { [templateID: number]: IUsageTableGroup };
	templateCharDataCollections: { [templateID: number]: ICharDataCollection };
	templateMetaData: { [templateID: number]: ICharacteristicMetaDataCollection };
	amountTemplateMetaData: { [templateID: number]: ICharacteristicMetaDataCollection };
	templateWorkflowSteps: { [templateID: number]: IWFStep[] };
}

export interface IUsageTableGroup {
	templateID: number;
	template: string;
	numFound: number;

	usages: IUsageDocument[];
}

export interface IUsageRow extends IListTableRow {
	relRecID: number;
	catalogItems?: { [recID: number]: string };
	projectItems?: { [recID: number]: string };
	contactItems?: { [recID: number]: string };
	mockItems?: { [recID: number]: string };
	deleteIndicator: boolean;
	activeIndicator: number;
	entityID: string;
	statusID: number;
	amounts?: IEntitySearchDoc[];
	amountNumFound?: number;
	templateID: number;
	sequenceNumber: number;
}

export interface IUsageDocument {
	relRecID: number;
	sequenceNumber: number;
	document: IEntitySearchDoc;
	amounts: { [relRecID: number]: IEntitySearchDoc };
	usageAmountsNumFound: number;
	// value is asset recID
	assets: number[];
	projects: number[];
	contacts: number[];
	mocks: number[];
}

export interface IUsageAmountDocument extends IEntitySearchDoc {
	invoiceRecID?: number;
}

export interface IGetUsageAmountDocumentsRequest {
	parentEntityID: string;
	relEntityID: string;
	page: number;
	pageSize: number;
}

export interface IGetAssociableInvoicesEntityRequest {
	entityID: string;
	amountTemplateIDs: number[];
	currencyCharValueID?: number;
}

export interface IAssociateAmountsToNewEntityInvoiceRequest {
	entityID: string;
	invoiceTemplateID: number;
	amountIDs: number[];
}

export interface IBulkCreateAmountsRequest {
	parentRelRecIDs: number[];
	amountTemplateID: string;
	numberOfRows: number;
	perCatalogChecked: boolean;
	splitMethod: number;
	splitValues: { [index: number]: string };
	dueDateMethod: number;
	timeSpan: string;
	startDate: string;
	dueDates: { [index: number]: string };
	copyTableValuesToAmounts: boolean;
}

//#endregion

export interface IDynamicReportColumnChar {
	characteristicID: number,
	label: string,
	tagName: string
}

export interface ISearchResultByRecordIDsRequest {
	charTypeID: number,
	recordIDs: string,
	rowCount?: number
}

export interface IEntityHierarchy {
	children: IEntityHierarchy[]
	root: IEntitySearchDoc;
}

export interface ITemplateHierarchy {
	templateID: number,
	templateLabel: string,
	level: number,
	numberOfRecords: number,
	directParentTemplates: number[],
	childTemplates: ITemplateHierarchy[]
}

export interface IBulkCreateAmountsResponse {
	entityIDs: string[];
}

//#region Modals
export interface IEntityModalModel {
	charDataCollection: ICharDataCollection,
	entityID?: string,
	relationships?: IEntityRelationship,
	idForRelationships?: string,
	relationshipCharTypeIDs?: number[],
	relationshipsRequired?: boolean,
	parentCurrencyCharData?: ICharDataValue
}
//#endregion

export interface IEntityRecIDTitle {
	recID: number,
	title: string
}

export interface IChildNotExistingParentEntityRelationshipList {
	recID: number;
	templateID: number;
	title: string;
}

export interface ISyncMetaDataOrPartiesToHierarchicalRecordsRequest {
	charTypeID: number;
	recID: number;
	partyID: number;
	contactID: number;
	characteristicIDs: number[];
	syncMode: number;
}

export interface ICatalogHierarchy {
	recID: number;
	title: string;
	directParentRecID: number;
	directParentTitle: string;
	topMostParentRecID: number;
	topMostParentTitle: string;
	hierarchyPath: string;
}

export interface IAssociatedItem {
	recID: number;
	associatedRecTitle: string;
	associatedRecID: number;
	partyID: number;
	charTypeID: number;
	associatedCharTypeID: number;
}

export interface IRecordAssociationCharacteristicTemplateRequired {
	divID: number;
	charTypeID: number;
	templateID: number;
	childCharTypeID: number;
}

//Audit History
export interface IAuditHistoryFilter {
	charTypes: IAuditHistoryCharType[];
	startDate: string;
	endDate: string;
	emails: string;
	page: number;
	rows: number;
	inUTC: boolean;
	lastAmendment: boolean;
}

export interface IAuditHistory {
	totalCount: number;
	isParent: number;
	isHeader: number;
	logIndicator: string;
	sequenceValue: string;
	operationID: number;
	operationLabel: string;
	divID: number;
	charTypeID: number;
	charTypeName: string;
	recID: number;
	title: string;
	templateID: number;
	templateName: string;
	characteristicID: number;
	lastUpdateID: number;
	userName: string;
	profileAvatar: string;
	auditDate: Date;
	localAuditDate: string;
	changedField: string;
}

export interface IAuditHistoryDetail {
	groupLabel: string;
	fields: IAuditHistoryDetailFields[];
}

export interface IAuditHistoryDetailFields {
	changedField: string;
	oldValue: string;
	newValue: string;
}

export interface IAuditHistoryCharType {
	charTypeID: number;
	charTypeName: string;
	groups: IAuditHistoryCharTypeGroup[];
}

export interface IAuditHistoryCharTypeGroup {
	groupID: number;
	groupLabel: string;
	characteristics: IAuditHistoryCharTypeGroupField[];
}

export interface IAuditHistoryCharTypeGroupField {
	characteristicID: number;
	characteristicLabel: string;
}
//--

//Conditional Field
export interface IConditionalField {
	conditionalFieldID: string;
	conditionalFieldTypeID: string;
	divID: number;
	charTypeID: number;
	charID: number;
	templateID: number;
	triggerCharID?: number;
	triggerGroupID?: number;
	triggerValue: string;
}
//--

//Relative Rights Profile
export interface IRelativeRightsProfile {
	name: string;
	entityID: string;
	rights: IMockRelatedEntityResponse;
}

export interface IMockRelatedEntityResponse {
	groups: IGroup[];
}

export interface IGroup {
	templateID: number;
	templateName: string;
	numFound: number;
	templateMetaData: ICharacteristicMetaDataCollection;
	items: IGroupItem[];
}

export interface IGroupItem {
	assetID: number;
	assets: number[];
	record: IEntity;
	status: IEntityStatus;
	charData: ICharDataValueFullName[];
}
export interface ICharDataValueFullName {
	_hash?: number;
	charactersticID: number;
	recordCharacteristicID: number;
	value: string;
	valueID?: number;
	ext?: ICharDataExtensionData;
}
//--

export interface IApiAuthKey {
	apiAuthID: string;
	divID: number;
	rlAccessKey: string;
	isActive: boolean;
	createdBy: number;
	created: Date;
	lastUpdatedBy: number;
	deletedBy: number;
	deleted: Date;
}export interface IDivAvailsInfo {
	divID: number;
	processedAt: string;
	dataUpdatedAt: string;
	status: string;
	progress: number[];
}

export interface IFinancialValidation {
	data: string[];
}

export interface IWorkflowValidateEncryptedString {
	charTypeID: number;
	recordID: number;
	title: string;
	status: string;
	validationResult: string;
	parentEntityID: string;
}

export interface IEmailActionBtnExecuteWorkflowResult {
	success: boolean;
	newStatusID: number;
	errorStep: string;
	message: string;
	newStatusLabel: string;
	userTitle: string;
}

export interface IEntityMergeField {
	preTag: string;
	tagLabel: string;
	recID: number;
	recChrLabel: string;
	dataTypeID: number;
	recordCount: number;
}

export interface ICopyEntityRequest {
	copyToCharTypeID: number;
	copyToTemplateID: number;
	copyRelationships: boolean;
	entityID: string;
	copyToTitle: string;
	copyParties: boolean;
}

export interface IBulkCopyEntityRequest {
	charTypeId: number;
	recIds: number[];
	templateId: number;
	prefix: string;
	parentRecId: number;
}

export interface IRecordAssocationParentChildTemplateAssociation {
	divID: number;
	charTypeID: number;
	templateID: number;
	templateLabel: string;
	isChildRel: number;
	recordCount: number;
}

export interface IFinancialPeriod {
	id: number;
	year: number;
	period: number;
	startDate: Date;
	endDate: Date;
	status: number;
}

export interface IBulkUploadFileHeaderMapping {
	templateID: number;
	characteristicID: number;
	fileColumnHeader: string;
	characteristicLabel: string;
	isMapped: boolean;
}


export interface IBulkUploadFileHeaders {
	fileHeaderMapping: { [key: number]: IBulkUploadFileHeaderMapping[] }
}


export interface ISavedBulkUploadV2HeaderMapping {
	mappingName: string;
	charTypeID: number;
	templateIDs: number[];
	mappedHeaders: IBulkUploadFileHeaderMapping[];
	isGlobal: number;
	mode: number;
}

export interface IBulkUploadSavedMapping {
	mappingID: number;
	mappingName: string;
}

export interface IRightsDimensions {
	charID: number;
	systemIndicatorID: number;
	charValueSourceID: string;
	charValueSetID: string;
	charValueSourceLabel: string;
}